// ============================================================================
// CrewAhead Maritime Platform - Candidates API Functions
// ============================================================================
// Public candidate directory for employer browsing and discovery.
// Includes list, detail, featured, shortlisted, filters, save, and contact.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type { PaginatedAnonymizedCandidates } from '../types/models';
import type { TaxonomyItem } from '../adapters';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Candidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'seafarer' | 'shore';
  avatarUrl: string | null;
  nationality: string | null;
  currentLocation: string | null;
  yearsOfExperience: number | null;
  availabilityStatus: string | null;
  isVerified: boolean;
  dateJoined: string;
  // Seafarer-specific
  seafarerProfile?: {
    currentRank: { rankName: string } | null;
    bio: string | null;
    preferredVesselTypes: Array<{ id: string; vesselTypeName: string }>;
  } | null;
  // Shore-specific
  shoreProfile?: {
    currentPosition: { positionName: string } | null;
    bio: string | null;
  } | null;
}

export interface CandidateDetail extends Candidate {
  phone: string | null;
  bio: string | null;
  languages: string[];
  skills: string[];
  expectedSalaryUsd: number | null;
  preferredContractDuration: string | null;
  availableFrom: string | null;
  // Seafarer detail
  seafarerProfile?: {
    currentRank: { rankName: string } | null;
    targetRank: { rankName: string } | null;
    bio: string | null;
    yearsOfExperience: number | null;
    availabilityStatus: string | null;
    preferredVesselTypes: Array<{ id: string; vesselTypeName: string }>;
    seaServices: Array<{
      vesselName: string;
      vesselType: string;
      rank: string;
      dateFrom: string;
      dateTo: string | null;
    }>;
    certifications: Array<{
      certName: string;
      issueDate: string;
      expiryDate: string | null;
    }>;
  } | null;
  // Shore detail
  shoreProfile?: {
    currentPosition: { positionName: string } | null;
    bio: string | null;
    yearsOfExperience: number | null;
    availabilityStatus: string | null;
    skills: string[];
  } | null;
}

export interface CandidateFilters {
  roles: Array<{ value: string; label: string }>;
  ranks: Array<{ id: number; rankId: string; rankName: string; level: string; department: number | null }>;
  shorePositions: Array<{ id: number; positionId: string; positionName: string; level: string; shoreCategory: number | null }>;
  shoreCategories: TaxonomyItem[];
  departments: TaxonomyItem[];
  vesselCategories: TaxonomyItem[];
  vesselTypes: TaxonomyItem[];
  nationalities: string[];
  availabilityStatuses: Array<{ value: string; label: string }>;
  sortOptions: Array<{ value: string; label: string }>;
}

export interface CandidateContactData {
  subject?: string;
  body: string;
}

export interface CandidateContactResponse {
  detail: string;
  messageId: number | null;
  conversationId?: number;
  candidate?: string;
}

export interface CandidateSaveResponse {
  detail: string;
  created?: boolean;
  deleted?: boolean;
}

// ─── List ────────────────────────────────────────────────────────────────────

/**
 * GET /candidates/
 *
 * Employer candidate directory with filtering and pagination.
 *
 * @param params - Optional filters and pagination
 * @returns Paginated list of candidates
 */
export async function getCandidates(params?: {
  role?: 'seafarer' | 'shore';
  rankId?: string;
  rankDepartmentId?: string;
  positionId?: string;
  shoreCategoryId?: string;
  vesselTypeId?: string;
  location?: string;
  availability?: string;
  nationality?: string;
  keyword?: string;
  experienceMin?: number;
  experienceMax?: number;
  verifiedOnly?: boolean;
  sortBy?: 'recent' | 'experience' | 'name' | 'profile_completion';
  page?: number;
  pageSize?: number;
}, signal?: AbortSignal): Promise<{
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  results: Candidate[];
}> {
  try {
    const query = new URLSearchParams();

    if (params?.role) query.set('role', params.role);
    if (params?.rankId) query.set('rank_id', params.rankId);
    if (params?.rankDepartmentId) query.set('rank_department_id', params.rankDepartmentId);
    if (params?.positionId) query.set('position_id', params.positionId);
    if (params?.shoreCategoryId) query.set('shore_category_id', params.shoreCategoryId);
    if (params?.vesselTypeId) query.set('vessel_type_id', params.vesselTypeId);
    if (params?.location) query.set('location', params.location);
    if (params?.availability) query.set('availability', params.availability);
    if (params?.nationality) query.set('nationality', params.nationality);
    if (params?.keyword) query.set('keyword', params.keyword);
    if (params?.experienceMin !== undefined) query.set('experience_min', String(params.experienceMin));
    if (params?.experienceMax !== undefined) query.set('experience_max', String(params.experienceMax));
    if (params?.verifiedOnly) query.set('verified_only', 'true');
    if (params?.sortBy) query.set('sort_by', params.sortBy);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CANDIDATES.LIST}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      results: Candidate[];
    }>(url, { signal });

    return {
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
      results: response.data.results,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Anonymized Pool Search ────────────────────────────────────────────────────

export interface AnonymizedCandidateFilters {
  role?: 'seafarer' | 'shore';
  rankId?: string;
  positionId?: string;
  shoreCategoryId?: string;
  vesselTypeId?: string;
  location?: string;
  availability?: string;
  nationality?: string;
  keyword?: string;
  experienceMin?: number;
  experienceMax?: number;
  verifiedOnly?: boolean;
  certificationIds?: string[];
  departmentId?: string;
  tradingAreaId?: string;
  contractTypeId?: string;
  contractDurationId?: string;
  tankerExperience?: boolean;
  salaryMin?: number;
  salaryMax?: number;
  skills?: string[];
  availableFrom?: string;
  sortBy?: 'recent' | 'experience' | 'name' | 'profile_completion';
  page?: number;
  pageSize?: number;
}

/**
 * GET /candidates/?anonymized=true
 *
 * Employer-only anonymized candidate pool browse.  Returns the same
 * non-identifying field set as the job-anchored matched candidates endpoint.
 *
 * @param params - Optional filters and pagination
 * @returns Paginated list of anonymized candidates
 */
export async function getAnonymizedCandidates(params?: AnonymizedCandidateFilters): Promise<PaginatedAnonymizedCandidates> {
  try {
    const query = new URLSearchParams();
    query.set('anonymized', 'true');

    if (params?.role) query.set('role', params.role);
    if (params?.rankId) query.set('rank_id', params.rankId);
    if (params?.positionId) query.set('position_id', params.positionId);
    if (params?.shoreCategoryId) query.set('shore_category_id', params.shoreCategoryId);
    if (params?.vesselTypeId) query.set('vessel_type_id', params.vesselTypeId);
    if (params?.location) query.set('location', params.location);
    if (params?.availability) query.set('availability', params.availability);
    if (params?.nationality) query.set('nationality', params.nationality);
    if (params?.keyword) query.set('keyword', params.keyword);
    if (params?.experienceMin !== undefined) query.set('experience_min', String(params.experienceMin));
    if (params?.experienceMax !== undefined) query.set('experience_max', String(params.experienceMax));
    if (params?.verifiedOnly) query.set('verified_only', 'true');

    if (params?.certificationIds?.length) query.set('certification_ids', params.certificationIds.join(','));
    if (params?.departmentId) query.set('department_id', params.departmentId);
    if (params?.tradingAreaId) query.set('trading_area_id', params.tradingAreaId);
    if (params?.contractTypeId) query.set('contract_type_id', params.contractTypeId);
    if (params?.contractDurationId) query.set('contract_duration_id', params.contractDurationId);
    if (params?.tankerExperience) query.set('tanker_experience', 'true');
    if (params?.salaryMin !== undefined) query.set('salary_min', String(params.salaryMin));
    if (params?.salaryMax !== undefined) query.set('salary_max', String(params.salaryMax));
    if (params?.skills?.length) query.set('skills', params.skills.join(','));
    if (params?.availableFrom) query.set('available_from', params.availableFrom);

    if (params?.sortBy) query.set('sort_by', params.sortBy);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const response = await apiClient.get<PaginatedAnonymizedCandidates & {
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>(`${API_ENDPOINTS.CANDIDATES.LIST}?${query.toString()}`);

    return {
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.pageSize ?? response.data.page_size,
      totalPages: response.data.totalPages ?? response.data.total_pages,
      results: response.data.results,
      gated: response.data.gated,
      reason: response.data.reason,
      minimum: response.data.minimum,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Reveal Identity ─────────────────────────────────────────────────────────

export interface RevealCandidateResponse {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  revealedAt?: string;
  // Gate-block payload
  detail?: string;
  code?: string;
  freeRevealsRemaining?: number;
  subscriptionRequired?: boolean;
}

/**
 * POST /candidates/reveal/
 *
 * Reveal the real identity behind an anonymized candidate card.
 * Sends the signed reveal_token issued with the anonymized payload.
 *
 * @param revealToken - Signed employer-scoped token from the anonymized card
 * @returns Revealed identity or a gate-block payload with subscriptionRequired=true
 */
export async function revealCandidate(revealToken: string): Promise<RevealCandidateResponse> {
  try {
    const response = await apiClient.post<RevealCandidateResponse>(
      API_ENDPOINTS.CANDIDATES.REVEAL,
      { reveal_token: revealToken }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export interface RevealGateStatus {
  isTrialActive: boolean;
  isSubscribed: boolean;
  revealsUsed: number;
  freeRevealsRemaining: number;
  subscriptionRequired: boolean;
}

/**
 * GET /candidates/reveal-status/
 *
 * Current employer reveal gate status: trial state, subscription state,
 * reveal usage, and remaining free reveals.
 *
 * @returns Reveal gate status for the authenticated employer
 */
export async function getRevealStatus(): Promise<RevealGateStatus> {
  try {
    const response = await apiClient.get<RevealGateStatus>(
      API_ENDPOINTS.CANDIDATES.REVEAL_STATUS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Detail ──────────────────────────────────────────────────────────────────

/**
 * GET /candidates/{id}/
 *
 * Public candidate detail view. Full profile with nested data.
 * No authentication required.
 *
 * @param id - Candidate user ID
 * @returns Candidate detail
 */
export async function getCandidate(id: string): Promise<CandidateDetail> {
  try {
    const response = await apiClient.get<CandidateDetail>(
      API_ENDPOINTS.CANDIDATES.DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Featured ────────────────────────────────────────────────────────────────

/**
 * GET /candidates/featured/
 *
 * Returns highly-experienced candidates for the candidate directory.
 * Requires an authenticated employer or admin — despite earlier docstring
 * claims, the backend view enforces IsEmployerOrAdmin, so anonymous calls
 * fail with 401/403. The /hire-crew landing page relies on this and renders
 * a gated fallback when the call fails.
 *
 * @param params - Optional pagination
 * @returns Paginated list of featured candidates
 */
export async function getFeaturedCandidates(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  results: Candidate[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CANDIDATES.FEATURED}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      results: Candidate[];
    }>(url);

    return {
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
      results: response.data.results,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Shortlisted ─────────────────────────────────────────────────────────────

/**
 * GET /candidates/shortlisted/
 *
 * Returns the current employer's shortlisted candidates.
 * Authentication + employer role required.
 *
 * @param params - Optional pagination
 * @returns Paginated list of shortlisted candidates
 */
export async function getShortlistedCandidates(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  results: Candidate[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.CANDIDATES.SHORTLISTED}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      results: Candidate[];
    }>(url);

    return {
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
      results: response.data.results,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Filters ─────────────────────────────────────────────────────────────────

/**
 * GET /candidates/filters/
 *
 * Returns available filter options for the candidate directory.
 * Useful for populating frontend filter dropdowns.
 *
 * @returns Available filter options
 */
export async function getCandidateFilters(): Promise<CandidateFilters> {
  try {
    const response = await apiClient.get<CandidateFilters>(
      API_ENDPOINTS.CANDIDATES.FILTERS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Save / Shortlist ────────────────────────────────────────────────────────

/**
 * POST /candidates/{id}/save/
 *
 * Add a candidate to the employer's shortlist.
 * Employer authentication required.
 *
 * @param id - Candidate user ID
 * @returns Save confirmation
 */
export async function saveCandidate(
  id: string
): Promise<CandidateSaveResponse> {
  try {
    const response = await apiClient.post<CandidateSaveResponse>(
      API_ENDPOINTS.CANDIDATES.SAVE(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /candidates/{id}/save/
 *
 * Remove a candidate from the employer's shortlist.
 * Employer authentication required.
 *
 * @param id - Candidate user ID
 * @returns Removal confirmation
 */
export async function unsaveCandidate(
  id: string
): Promise<CandidateSaveResponse> {
  try {
    const response = await apiClient.delete<CandidateSaveResponse>(
      API_ENDPOINTS.CANDIDATES.SAVE(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Contact ─────────────────────────────────────────────────────────────────

/**
 * POST /candidates/{id}/contact/
 *
 * Send a contact request to a candidate. Employer-only.
 * Integrates with the messaging system when available.
 *
 * @param id - Candidate user ID
 * @param data - Contact message data
 * @returns Contact confirmation with message details
 */
export async function contactCandidate(
  id: string,
  data: CandidateContactData
): Promise<CandidateContactResponse> {
  try {
    const response = await apiClient.post<CandidateContactResponse>(
      API_ENDPOINTS.CANDIDATES.CONTACT(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
